/**
 * Map a failed POST /api/auth/login into the message the sign-in form shows.
 *
 * Only a 401 (and a 400/422 request rejected before the password was even
 * checked) means the credentials were wrong. A 5xx, a timeout or a network
 * failure is an OUTAGE, and telling the user "Invalid email or password" in
 * that case sends them off resetting a password that was never the problem
 * (kanban d2f93d2e: a deleted staging database read as bad credentials).
 */

import axios from 'axios';

export const LOGIN_BAD_CREDENTIALS = 'Invalid email or password';
export const LOGIN_ACCOUNT_INACTIVE = 'Your account is not active. Please contact your administrator.';
export const LOGIN_SERVICE_UNAVAILABLE = 'Service temporarily unavailable. Please try again in a moment.';
export const LOGIN_TIMED_OUT = 'The server took too long to respond. Please try again in a moment.';
export const LOGIN_NETWORK_ERROR = 'Unable to reach the server. Please check your connection and try again.';
export const LOGIN_UNEXPECTED = 'Unable to sign in. Please try again.';

/** FastAPI puts a human-readable string in `detail`; a 422 carries a list instead. */
function serverDetail(data: unknown): string | null {
  if (typeof data !== 'object' || data === null) return null;
  const detail = (data as { detail?: unknown }).detail;
  return typeof detail === 'string' && detail.trim() ? detail : null;
}

export function loginErrorMessage(err: unknown): string {
  if (!axios.isAxiosError(err)) return LOGIN_UNEXPECTED;

  const status = err.response?.status;
  if (status === undefined) {
    // No HTTP response at all: the request never completed.
    return err.code === 'ECONNABORTED' || err.code === 'ETIMEDOUT'
      ? LOGIN_TIMED_OUT
      : LOGIN_NETWORK_ERROR;
  }

  if (status === 401 || status === 400 || status === 422) return LOGIN_BAD_CREDENTIALS;
  if (status === 403) return serverDetail(err.response?.data) ?? LOGIN_ACCOUNT_INACTIVE;
  if (status >= 500) return serverDetail(err.response?.data) ?? LOGIN_SERVICE_UNAVAILABLE;
  return serverDetail(err.response?.data) ?? LOGIN_UNEXPECTED;
}
